<script setup lang="ts">
import { tpgIconSearch } from '@tpg/grammar-web-icons';
import { useDebounceFn } from '@vueuse/core';
import {
  computed,
  nextTick,
  onMounted,
  ref,
  toRaw,
  toRefs,
} from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarBadge,
  GrammarLocalLoader,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';
import type { GeocodingFeature } from '@nora-monorepo/nora-api-client';

import GeoSearchResultItem from '#nora-ui/components/geo-search/GeoSearchResultItem.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';

export interface GeoSearchProps {
  mapId: string;
  focusOnMount?: boolean;
  showSearchResults?: boolean;
  label?: string;
  placeholder?: string;
}

const props = withDefaults(defineProps<GeoSearchProps>(), {
  focusOnMount: true,
  showSearchResults: true,
  label: '',
  placeholder: '',
});

const emit = defineEmits([
  'featureSelected',
  'searchQueryUpdated',
  'searchResultsUpdated',
  'resetCompleted',
]);
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(props.mapId);

const { t } = useI18n();

const label = computed(() => {
  return props.label ?? t('geoSearch.defaultLabel');
});

const placeholder = computed(() => {
  return props.placeholder ?? t('geoSearch.defaultPlaceholder');
});

const { focusOnMount, showSearchResults } = toRefs(props);

const hasSearched = ref(false);
const isSearching = ref(false);
const query = ref<string>('');
const searchFieldRef = ref<HTMLElement>();
const searchResults = ref<GeocodingFeature[]>([]);

const searchDebounced = useDebounceFn(
  async (query: string) => {
    try {
      isSearching.value = true;

      emit('searchQueryUpdated', query);

      const result = await map.state.geoSearch.getAdvancedSearchResults(query);

      const features = result?.geoJson?.features ?? [];

      searchResults.value = [...features];

      emit('searchResultsUpdated', searchResults.value);
    }
    finally {
      isSearching.value = false;
      hasSearched.value = true;
    }
  },
  300,
  { maxWait: 1000 },
);

const isValidSearchQuery = computed(() => {
  return query.value.trim().length > 0;
});

async function focusSearchField() {
  await nextTick();
  searchFieldRef.value?.focus();
}

function resetSearch() {
  query.value = '';
  searchResults.value = [];
  hasSearched.value = false;
  isSearching.value = false;

  emit('searchResultsUpdated', []);
  emit('searchQueryUpdated', '');
  emit('resetCompleted');
}

onMounted(() => {
  if (focusOnMount.value) {
    focusSearchField();
  }
});

async function onSearchQueryUpdated(value: string) {
  query.value = value;

  isSearching.value = true;

  await searchDebounced(value);
}

function onClearButtonClick(value: string) {
  if (value === '') {
    resetSearch();
  }
}

function handleSearchResultSelected(searchResult: GeocodingFeature) {
  emit('featureSelected', toRaw(searchResult));
}

defineExpose({
  resetSearch,
});
</script>

<template>
  <div class="w-full">
    <GrammarTextField
      ref="searchFieldRef"
      :value="query"
      :leading-icon-template="tpgIconSearch"
      :label="label"
      :placeholder="placeholder"
      :clear-button="true"
      @input="onSearchQueryUpdated"
      @change="onClearButtonClick"
    />
    <div
      v-if="isValidSearchQuery && hasSearched"
      class="bg-light-bg-secondary dark:bg-dark-bg-secondary p-1 rounded-b-md pb-2"
    >
      <div
        v-if="searchResults.length === 0"
        class="p-2"
      >
        <GrammarBadge
          label="No results found..."
          type="accent"
          emphasis="no-background"
        />
      </div>
      <div
        v-else-if="isSearching"
        class="pt-2"
      >
        <GrammarLocalLoader />
      </div>
      <div v-else-if="showSearchResults">
        <GrammarBadge
          label="Places"
          type="accent"
          emphasis="no-background"
        />
        <div class="max-h-[50vh] overflow-x-hidden overflow-y-auto shrink">
          <GeoSearchResultItem
            v-for="(searchResult, index) in searchResults"
            :key="index"
            :menu-item-key="index"
            :search-result="searchResult"
            @search-result-selected="
              handleSearchResultSelected
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>
